import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useMemo } from "react";
import { mergeProps, useListBox } from "react-aria";
import { useListState } from "react-stately";
import { tv } from "tailwind-variants";

import { useDomRef } from "../../hooks/useDomRef";
import { ListBoxItem, ListBoxItemInternal } from "./ListBoxItem";
import { ListBoxSection, ListBoxSectionInternal } from "./ListBoxSection";
import { ListBoxSelectAll } from "./ListBoxSelectAll";

import type { ComponentPropsWithoutRef, ForwardedRef, JSX, ReactNode } from "react";
import type { AriaListBoxOptions, AriaListBoxProps } from "react-aria";
import type { Key, ListState } from "react-stately";
import type { VariantProps } from "tailwind-variants";
import type { ReactRef } from "../../utils/types";

const listbox = tv({
  slots: {
    wrapper: "relative flex w-full flex-col gap-1 p-1",
    list: "m-0 flex w-full flex-col gap-0.5 p-0 outline-none",
    emptyContent: "size-full px-2 py-1.5 text-start text-gray-400 dark:text-gray-500",
    loader:
      "flex w-full items-center justify-center py-1 text-gray-300 [--fa-animation-duration:0.5s] dark:text-gray-600",
  },
  variants: {
    isLoading: {
      false: {
        loader: "hidden",
      },
    },
  },
});

type ListBoxVariantProps = VariantProps<typeof listbox>;

export interface SelectAllOptions {
  isEnabled: boolean;
  label?: string;
  sectionTitle?: string;
}

export interface ListBoxProps<T>
  extends Omit<ComponentPropsWithoutRef<"ul">, "children" | "onBlur" | "onFocus" | "autoFocus">,
    AriaListBoxProps<T>,
    ListBoxVariantProps {
  state?: ListState<T>;
  /**
   *  Provides content to display when there are no items.
   * @default "No results."
   */
  emptyContent?: ReactNode;
  /**
   * Whether to not display the empty content when there are no items.
   * @default false
   */
  hideEmptyContent?: boolean;
  /**
   * Whether the listbox items should use virtual focus instead of being focused directly.
   */
  shouldUseVirtualFocus?: boolean;
  /** Whether selection should occur on press up instead of press down. */
  shouldSelectOnPressUp?: boolean;
  /** Whether options should be focused when the user hovers over them. */
  shouldFocusOnHover?: boolean;
  /** Whether the item should display the same "hover" styles as when it is focused. */
  shouldHighlightOnFocus?: boolean;

  isLoading?: boolean;

  loaderRef?: ReactRef<HTMLDivElement>;

  selectAll?: SelectAllOptions;

  /** The current input value for filtering */
  inputValue?: string;

  /**
   * Keys of items that should be hidden from the list.
   * Useful for implementing search/filter functionality.
   */
  hiddenKeys?: Set<Key>;

  /**
   * Whether to disable animations.
   * @default false
   */
  disableAnimation?: boolean;

  linkBehavior?: AriaListBoxOptions<T>["linkBehavior"];
}

const ListBoxImpl = <T extends object>(
  props: ListBoxProps<T>,
  ref: ForwardedRef<HTMLUListElement>
) => {
  const {
    className,
    children,
    state: propState,
    emptyContent: emptyContentProp = "No results.",
    hideEmptyContent = false,
    shouldHighlightOnFocus = false,
    shouldUseVirtualFocus = false,
    shouldSelectOnPressUp = false,
    shouldFocusOnHover = false,
    onAction,
    onSelectionChange,
    isLoading,
    loaderRef,
    autoFocus,
    selectAll: {
      isEnabled: isSelectAllEnabled,
      label: selectAllLabel,
      sectionTitle: selectAllSectionTitle,
    } = {},
    inputValue = "",
    hiddenKeys,
    // Destructure these props to prevent them from being passed to DOM elements
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    disableAnimation: _disableAnimation,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    disallowEmptySelection: _disallowEmptySelection,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    linkBehavior: _linkBehavior,
    ...otherProps
  } = props;

  const domRef = useDomRef(ref);
  const loadingDomRef = useDomRef(loaderRef);

  const innerState = useListState(props);
  const state = propState || innerState;

  const { listBoxProps } = useListBox(props, state, domRef);

  const slots = useMemo(() => listbox({ isLoading }), [isLoading]);

  const listProps = useMemo(
    () => ({
      ref: domRef,
      className: slots.list(),
      ...mergeProps(listBoxProps, otherProps),
    }),
    [domRef, slots, listBoxProps, otherProps]
  );

  // Get filtered keys when there's a search term
  const filteredKeys = useMemo(() => {
    if (!inputValue.trim()) return undefined;
    const keys = new Set<Key>();
    for (const item of state.collection) {
      if (item.type === "item") {
        keys.add(item.key);
      }
    }
    return keys;
  }, [inputValue, state.collection]);

  // Handle selecting filtered items
  const handleSelectFiltered = useCallback(
    (keys: Set<Key>) => {
      const currentSelection = new Set(state.selectionManager.selectedKeys);
      keys.forEach(key => currentSelection.add(key));
      state.selectionManager.setSelectedKeys(currentSelection);
    },
    [state.selectionManager]
  );

  return (
    <div className={slots.wrapper({ className })}>
      <ul {...listProps}>
        {!state.collection.size && (
          <li>
            <div className={slots.emptyContent()}>{emptyContentProp}</div>
          </li>
        )}
        {isSelectAllEnabled && (
          <ListBoxSelectAll
            state={state}
            label={selectAllLabel}
            sectionTitle={selectAllSectionTitle}
            inputValue={inputValue}
            filteredKeys={filteredKeys}
            onSelectFiltered={handleSelectFiltered}
          />
        )}
        {[...state.collection].map(item => {
          // Skip items that are in hiddenKeys
          if (hiddenKeys?.has(item.key)) {
            return null;
          }

          if (item.type === "section") {
            // For sections, check if any children are visible
            const hasVisibleChildren = hiddenKeys
              ? [...item.childNodes].some(child => !hiddenKeys.has(child.key))
              : true;
            if (!hasVisibleChildren) {
              return null;
            }
            return (
              <ListBoxSectionInternal
                key={item.key}
                item={item}
                state={state}
                hiddenKeys={hiddenKeys}
                {...item.props}
              />
            );
          }
          let listBoxItem = (
            <ListBoxItemInternal key={item.key} item={item} state={state} {...item.props} />
          );

          if (item.wrapper) {
            listBoxItem = item.wrapper(listBoxItem);
          }

          return listBoxItem;
        })}
        <div ref={loadingDomRef} className={slots.loader()}>
          <FontAwesomeIcon icon={faSpinnerThird} spin size="lg" />
        </div>
      </ul>
    </div>
  );
};

const WrappedListBox = forwardRef(ListBoxImpl) as <T = object>(
  props: ListBoxProps<T> & { ref?: ForwardedRef<HTMLUListElement> }
) => JSX.Element;

/**
 * A container displaying a list of options that the user can select one or more of.
 *
 * ListBox follows the react-aria Collection Components API (https://react-spectrum.adobe.com/react-stately/collections.html).
 */
export const ListBox = WrappedListBox as typeof WrappedListBox & {
  displayName: string;
  Item: typeof ListBoxItem;
  Section: typeof ListBoxSection;
};
ListBox.displayName = "ListBox";
ListBox.Item = ListBoxItem;
ListBox.Section = ListBoxSection;
